// 구독 등급 정의
export const SubscriptionTier = {
  STANDARD: 'standard',
  PRO: 'pro',
  ULTRA: 'ultra',
  ENTERPRISE: 'enterprise',
} as const;

export type SubscriptionTier = typeof SubscriptionTier[keyof typeof SubscriptionTier];

// 구독 기간 정의
export const SubscriptionDuration = {
  ONE_MONTH: 1,
  THREE_MONTHS: 3,
  SIX_MONTHS: 6,
  TWELVE_MONTHS: 12,
  UNLIMITED: -1, // 무제한
} as const;

export type SubscriptionDuration = typeof SubscriptionDuration[keyof typeof SubscriptionDuration];

// 구독 상태 정의
export const SubscriptionStatus = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  TRIAL: 'trial',
  PENDING: 'pending', // 승인 대기중
} as const;

export type SubscriptionStatus = typeof SubscriptionStatus[keyof typeof SubscriptionStatus];

// 구독 제한사항 정의
export interface SubscriptionLimits {
  monthlyProjects: number;
  maxFileSize: number; // MB 단위
  qualityOptions: {
    standard: boolean;
    high: boolean;
    ultra: boolean;
  };
  sceneSizeOptions: {
    small: boolean;
    medium: boolean;
    large: boolean;
    world: boolean;
  };
  autoColorCorrection: boolean;
  // Spark.js 2.0 LoD Profile 옵션 (Phase 3)
  //   fast: 모든 플랜 (default)
  //   balanced: Pro+
  //   max: Ultra+
  lodProfileOptions: {
    fast: boolean;
    balanced: boolean;
    max: boolean;
  };
  // High Precision (ExtSplats float32 좌표) — Ultra+ 전용
  highPrecision: boolean;
}

// 구독 등급별 제한사항 설정
export const SUBSCRIPTION_LIMITS: Record<SubscriptionTier, SubscriptionLimits> = {
  [SubscriptionTier.STANDARD]: {
    monthlyProjects: 5,
    maxFileSize: 500, // 500MB
    qualityOptions: {
      standard: true,
      high: true,
      ultra: false,
    },
    sceneSizeOptions: {
      small: true,
      medium: true,
      large: true,
      world: false,
    },
    autoColorCorrection: false,
    lodProfileOptions: {
      fast: true,
      balanced: false,
      max: false,
    },
    highPrecision: false,
  },
  [SubscriptionTier.PRO]: {
    monthlyProjects: 5,
    maxFileSize: 1024, // 1GB
    qualityOptions: {
      standard: true,
      high: true,
      ultra: true,
    },
    sceneSizeOptions: {
      small: true,
      medium: true,
      large: true,
      world: false,
    },
    autoColorCorrection: true,
    lodProfileOptions: {
      fast: true,
      balanced: true,
      max: false,
    },
    highPrecision: false,
  },
  [SubscriptionTier.ULTRA]: {
    monthlyProjects: 10,
    maxFileSize: 2048, // 2GB
    qualityOptions: {
      standard: true,
      high: true,
      ultra: true,
    },
    sceneSizeOptions: {
      small: true,
      medium: true,
      large: true,
      world: true,
    },
    autoColorCorrection: true,
    lodProfileOptions: {
      fast: true,
      balanced: true,
      max: true,
    },
    highPrecision: true,
  },
  [SubscriptionTier.ENTERPRISE]: {
    monthlyProjects: -1, // 무제한
    maxFileSize: -1, // 무제한
    qualityOptions: {
      standard: true,
      high: true,
      ultra: true,
    },
    sceneSizeOptions: {
      small: true,
      medium: true,
      large: true,
      world: true,
    },
    autoColorCorrection: true,
    lodProfileOptions: {
      fast: true,
      balanced: true,
      max: true,
    },
    highPrecision: true,
  },
};

// 사용자 구독 정보 (Firestore에 저장될 구조)
export interface UserSubscription {
  userId: string;
  userEmail: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  startDate: Date;
  endDate: Date | null; // null이면 무제한
  trialEndDate?: Date; // 체험 기간 종료일
  hasUsedTrial?: boolean; // Pro 체험판 사용 여부 (계정당 1회)
  trialStartDate?: Date; // 체험판 시작일
  createdAt: Date;
  updatedAt: Date;
  // 결제 정보 (나중에 확장 가능)
  paymentInfo?: {
    method?: string;
    lastPaymentDate?: Date;
    nextPaymentDate?: Date;
  };
}

// 월별 사용량 추적 (Firestore에 저장될 구조)
export interface MonthlyUsage {
  userId: string;
  userEmail: string;
  year: number;
  month: number; // 1-12
  projectsCreated: number;
  storageUsed: number; // MB 단위
  lastProjectDate?: Date;
  projects: string[]; // 프로젝트 ID 목록
  createdAt: Date;
  updatedAt: Date;
}

// Plan 변경 요청 (Firestore에 저장될 구조)
export interface PlanChangeRequest {
  id?: string;
  userId: string;
  userEmail: string;
  currentTier: SubscriptionTier;
  requestedTier: SubscriptionTier;
  duration: SubscriptionDuration;
  title: string;
  description: string; // 최소 10자
  organizationName: string;
  contactName: string; // 담당자 이름
  phoneNumber: string;
  status: 'pending' | 'approved' | 'rejected';
  requestDate: Date;
  processedDate?: Date;
  processedBy?: string; // 처리한 관리자 ID
  processedByEmail?: string; // 처리한 관리자 이메일
  processNote?: string; // 관리자 메모
}

// 관리자 구독 작업 로그 타입
export const SubscriptionLogAction = {
  ADMIN_RESET: 'admin_reset', // 관리자가 구독 초기화
  ADMIN_FORCE_SET: 'admin_force_set', // 관리자가 구독 강제 설정
  PLAN_APPROVED: 'plan_approved', // Plan 변경 요청 승인
  PLAN_REJECTED: 'plan_rejected', // Plan 변경 요청 거부
  TRIAL_STARTED: 'trial_started', // 체험판 시작
  SUBSCRIPTION_EXPIRED: 'subscription_expired', // 구독 만료
} as const;

export type SubscriptionLogAction = typeof SubscriptionLogAction[keyof typeof SubscriptionLogAction];

// 구독 관리 로그 (Firestore에 저장될 구조)
export interface SubscriptionLog {
  id?: string;
  userId: string;
  userEmail: string;
  action: SubscriptionLogAction;
  previousTier?: SubscriptionTier;
  newTier?: SubscriptionTier;
  duration?: SubscriptionDuration;
  adminId?: string; // 관리자가 작업한 경우
  adminEmail?: string;
  note?: string; // 관리자 메모
  createdAt: Date;
}

// 사용자 통계 정보 (관리자 페이지용)
export interface UserWithStats {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  disabled: boolean;
  createdAt: string;
  lastSignInTime: string | null;
  subscription: UserSubscription | null;
  projectCount: number;
  lastProjectDate: Date | null;
}

// 구독 정보는 Firestore에서만 관리 (Custom Claims에서 제거됨)